//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Laboratory Branding Composable
 *
 * Centralized management of laboratory identity (name, logo, favicon).
 *
 * Responsibilities:
 * - Initialize and cache laboratory branding data
 * - Manage blob URL lifecycle (create/revoke)
 * - Handle locale changes with single watcher (no duplicates)
 * - Provide ready-to-use computed properties
 * - Auto-cleanup on unmount
 *
 * Benefits:
 * - Eliminates duplicate locale watchers
 * - Centralized blob URL management
 * - Single source of truth for branding
 * - Automatic SSR compatibility via useState
 *
 * @param key - Unique key for state isolation (default: "laboratory")
 */
export const useLaboratoryBranding = (key = "laboratory") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  const { locale } = useI18n();
  const { fetchName, fetchLogoByFormat, laboratoryName } = useLaboratory(key);
  const { handleError } = useErrorBoundary();
  const { withAbort, abortAll } = useAbortableMultiFetch();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  const logoUrl = useState<string>(`${key}.logoUrl`, () => "");
  const faviconUrl = useState<string>(`${key}.faviconUrl`, () => "");
  const isInitialized = useState<boolean>(`${key}.initialized`, () => false);
  const loading = useState<boolean>(`${key}.branding.loading`, () => false);

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  /**
   * Check if all branding data is loaded
   */
  const hasData = computed(() => Boolean(laboratoryName.value && logoUrl.value));

  /**
   * Check if logo is cached (blob URL exists)
   */
  const hasLogo = computed(() => Boolean(logoUrl.value));

  /**
   * Check if name is cached for current locale
   */
  const hasName = computed(() => Boolean(laboratoryName.value));

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Initialize laboratory branding
   *
   * Fetches logo and name in parallel if not cached.
   * Safe to call multiple times (idempotent).
   */
  const initialize = async (): Promise<void> => {
    // Check if we need to fetch logo
    // Blob URLs become invalid after page reload, so always re-fetch if it's a blob URL
    const needsLogo = !logoUrl.value || logoUrl.value.startsWith("blob:");
    const needsName = !laboratoryName.value;

    if (!needsLogo && !needsName) {
      isInitialized.value = true;
      return;
    }

    loading.value = true;

    try {
      const promises: Promise<any>[] = [];

      // Fetch logo if not cached or if blob URL needs refresh
      if (needsLogo) {
        promises.push(
          withAbort((signal) => fetchLogoByFormat("png", signal)).then(async (logoBlob) => {
            if (logoBlob) {
              // Revoke old blob URL before creating new one
              if (logoUrl.value?.startsWith("blob:")) {
                URL.revokeObjectURL(logoUrl.value);
              }
              logoUrl.value = URL.createObjectURL(logoBlob);

              // Convert blob to data URL for favicon
              const reader = new FileReader();
              reader.onloadend = () => {
                faviconUrl.value = reader.result as string;
              };
              reader.readAsDataURL(logoBlob);
            } else {
              console.warn("[useLaboratoryBranding] Logo blob is null");
            }
          }),
        );
      }

      // Fetch name if not cached for current locale
      if (needsName) {
        promises.push(withAbort((signal) => fetchName(locale.value, signal, false)));
      }

      await Promise.all(promises);
      isInitialized.value = true;
    } catch (err) {
      console.error("[useLaboratoryBranding] Initialization error:", err);
      handleError(err, {
        operation: "initializeLaboratoryBranding",
        showToast: false,
      });
    } finally {
      loading.value = false;
    }
  };

  /**
   * Refresh name for new locale
   *
   * Called automatically by locale watcher.
   * Can be called manually for force refresh.
   */
  const refreshName = async (newLocale?: string): Promise<void> => {
    if (!isInitialized.value) return;

    try {
      await withAbort((signal) => fetchName(newLocale || locale.value, signal, false));
    } catch (err) {
      console.error("Failed to refresh laboratory name:", err);
    }
  };

  //
  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================
  //

  onUnmounted(() => {
    // DO NOT revoke blob URL here - it's shared across layouts
    // The blob URL persists for the session and is cleaned up when page closes
    // Only abort pending requests
    abortAll();
  });

  //
  // ============================================================================
  // RETURN PUBLIC API
  // ============================================================================
  //

  return {
    // State (readonly)
    logoUrl: readonly(logoUrl),
    faviconUrl: readonly(faviconUrl),
    laboratoryName: readonly(laboratoryName),
    loading: readonly(loading),
    isInitialized: readonly(isInitialized),

    // Computed
    hasData,
    hasLogo,
    hasName,

    // Methods
    initialize,
    refreshName,
  };
};
